import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp, Send, Play, CheckCircle2, AlertCircle, Clock, X, ExternalLink, BookOpen, Bookmark } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import VideoCarousel from '../components/VideoCarousel';
import { getFallbackDayInLifeVideos } from '../utils/dayInLifeVideos';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { toast } from 'sonner';
import './RoleDetailPage.css';

const RoleDetailPage = () => {
  const { roadmapId, roleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  // Initialize role from state if available, otherwise null
  const initialRole = location.state?.roleData || null;
  const [role, setRole] = useState(initialRole);
  const [deepDive, setDeepDive] = useState(null);
  const [loading, setLoading] = useState(!location.state?.roleData);
  const [loadingDeepDive, setLoadingDeepDive] = useState(true);
  const [videos, setVideos] = useState(getFallbackDayInLifeVideos(initialRole?.title || 'career'));
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [chatError, setChatError] = useState('');
  const [isSaved, setIsSaved] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    overview: true,
    topCompanies: true,
    goodFit: true,
    apply: true,
    portfolio: true,
    pathways: true,
    questions: true
  });
  
  const chatMessagesRef = useRef(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';

  useEffect(() => {
    loadRole();
  }, [roadmapId, roleId]);

  useEffect(() => {
    if (role && user) {
      loadDeepDive();
      loadVideos(role.title);
      
      // Check if role is saved in Supabase
      checkIfSaved();
    }
  }, [role?.id, role?.title, user]);

  const checkIfSaved = async () => {
    try {
        const { data, error } = await supabase
            .from('saved_roles')
            .select('id')
            .eq('user_id', user.id)
            .eq('role_title', role.title)
            .maybeSingle();
            
        if (error) {
            // Ignore 404 (table missing), 406 (not acceptable), or PGRST116 (0 rows - normal)
            // These are non-critical errors for optional bookmarking feature
            if (error.code !== '406' && error.code !== 'PGRST116' && error.code !== '42P01') {
                 // 42P01 = relation does not exist (table missing)
                 console.warn("Could not check saved status:", error.message);
            }
            return;
        }
        setIsSaved(!!data);
    } catch (e) {
        // Silently fail for missing table/network issues - bookmarking is optional
        if (e.code !== '42P01') { // Only log if it's not a missing table error
            console.warn("Error checking saved status", e);
        }
    }
  };

  const handleSaveRole = async () => {
    if (!user) {
        toast.error("Please log in to save roles");
        return;
    }

    try {
        if (isSaved) {
            // Delete
            const { error } = await supabase
                .from('saved_roles')
                .delete()
                .eq('user_id', user.id)
                .eq('role_title', role.title);
            
            if (error) {
                // Handle missing table gracefully
                if (error.code === '42P01' || error.code === '404') {
                    console.warn("Bookmarking feature not available (table missing)");
                    toast.error("Bookmarking feature is not set up yet");
                    return;
                }
                throw error;
            }
            setIsSaved(false);
            toast.success("Role removed from bookmarks");
        } else {
            // Insert
            const { error } = await supabase
                .from('saved_roles')
                .insert({
                    user_id: user.id,
                    role_title: role.title,
                    role_data: role,
                    roadmap_id: roadmapId
                });
            
            if (error) {
                // Handle missing table gracefully
                if (error.code === '42P01' || error.code === '404') {
                    console.warn("Bookmarking feature not available (table missing)");
                    toast.error("Bookmarking feature is not set up yet");
                    return;
                }
                throw error;
            }
            setIsSaved(true);
            toast.success("Role saved to bookmarks");
        }
    } catch (err) {
        console.error("Error saving role:", err);
        toast.error("Failed to update bookmark");
    }
  };

  // Auto-scroll chat
  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [role?.chat, sendingMessage]);

  const loadRole = async () => {
    // If we have role data from state (e.g. from Discover page), use it
    if (location.state?.roleData) {
      setRole(location.state.roleData);
      setLoading(false);
      return;
    }

    if (!roadmapId || !roleId) return;
    
    // Only show the full-page loading state if we truly have nothing yet
    if (!role) {
      setLoading(true);
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/roadmaps/${roadmapId}/roles/${roleId}`);
      if (response.ok) {
        const data = await response.json();
        setRole(prev => ({ ...prev, ...data.role }));
      } else {
        throw new Error(`Failed to load role: ${response.status}`);
      }
    } catch (error) {
      console.error('Error loading role:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadVideos = async (titleOverride = null) => {
    setLoadingVideos(true);
    const roleTitle = titleOverride || role?.title || 'Career';
    const fallbackVideos = getFallbackDayInLifeVideos(roleTitle);
    setVideos(prev => (prev && prev.length ? prev : fallbackVideos));

    try {
      const response = await fetch(`${BACKEND_URL}/api/search-videos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: roleTitle })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.videos && data.videos.length > 0) {
          setVideos(data.videos);
        } else {
          setVideos(fallbackVideos);
        }
      } else {
        setVideos(fallbackVideos);
      }
    } catch (error) {
      console.error('Error loading videos:', error);
      setVideos(fallbackVideos);
    } finally {
      setLoadingVideos(false);
    }
  };

  const loadDeepDive = async () => {
    setLoadingDeepDive(true);
    try {
      // If we have a roadmapId, use the specific endpoint
      if (!roadmapId) {
        if (roleId && roleId !== 'new') {
             throw new Error("No roadmap context for deep dive");
        } else {
             throw new Error("New role, generating local deep dive");
        }
      }

      // Pass title as query param fallback in case role isn't found in roadmap yet
      const roleTitle = role?.title || initialRole?.title || '';
      const titleParam = roleTitle ? `?title=${encodeURIComponent(roleTitle)}` : '';
      let url = `${BACKEND_URL}/api/roadmaps/${roadmapId}/roles/${roleId}/deep-dive${titleParam}`;

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to load deep-dive: ${response.status}`);
      }
      const data = await response.json();
      const rawData = data.data || data.deep_dive;

      // Normalize data if it's the old flat structure
      let normalizedData = rawData;
      
      // Check if we received the flat structure (missing career_overview)
      if (rawData && !rawData.career_overview) {
          normalizedData = {
              career_overview: {
                  what_it_is: role?.description || "A dynamic role focused on professional growth and impact.",
                  day_in_the_life: (rawData.day_in_life || []).map(d => typeof d === 'string' ? d : `${d.time}: ${d.activity}`),
                  what_youll_work_on: rawData.tools || ["Strategic Planning", "Execution", "Analysis"],
                  where_this_role_exists: ["Technology", "Corporate", "Startups"]
              },
              why_youre_a_good_fit: rawData.pros || [],
              what_you_need_to_apply: {
                  required_skills: (role?.skills || []).map(s => ({ skill: s, status: 'has', reason: 'Based on your profile' })),
                  hiring_manager_insights: ["Look for practical experience", "Cultural fit is important", "Showcase your portfolio"],
                  application_checklist: ["Update Resume", "Prepare Portfolio", "Write Cover Letter"]
              },
              portfolio_projects: rawData.portfolio_projects || [],
              pathways: {
                  stable: { description: "Standard corporate progression", steps: ["Junior Level", "Mid Level", "Senior Level"] },
                  adventurous: { description: "High-growth or startup path", steps: ["Early Employee", "Team Lead", "Head of Dept"] },
                  risky: { description: "Entrepreneurial path", steps: ["Freelance", "Founder", "Scale"] }
              },
              guided_questions: ["What is the salary range?", "What are the growth opportunities?", "What skills are most critical?"]
          };
      }

      setDeepDive(normalizedData);
    } catch (error) {
      
      // Use personalized "why it fits" tags if available from the role object
      let whyFits = [
        `Matches your skills in ${role?.skills?.[0] || "technology"}`,
        "Aligns with your career growth goals",
        "Leverages your problem-solving abilities",
        "Fits your interest in innovation"
      ];
      
      if (role?.why_it_fits) {
        if (Array.isArray(role.why_it_fits)) {
           whyFits = role.why_it_fits;
        } else if (typeof role.why_it_fits === 'string' && role.why_it_fits.includes('•')) {
           whyFits = role.why_it_fits.split('•').map(s => s.trim());
        } else if (typeof role.why_it_fits === 'string') {
           whyFits = [role.why_it_fits];
        }
      }

      // Fallback data if fetch fails or no context
      setDeepDive({
        career_overview: {
          what_it_is: role?.description || "A dynamic role focused on solving complex problems and driving value.",
          day_in_the_life: ["Collaborating with cross-functional teams", "Analyzing data to inform decisions", "Continuous learning and upskilling", "Presenting findings to stakeholders"],
          what_youll_work_on: ["Strategic initiatives", "Product development", "Process optimization"],
          where_this_role_exists: ["Technology Companies", "Startups", "Enterprise Organizations"]
        },
        why_youre_a_good_fit: whyFits,
        what_you_need_to_apply: {
          required_skills: (role?.skills || ["Communication", "Leadership", "Analysis"]).map(s => ({ skill: s, status: 'has', reason: 'Based on your profile' })),
          hiring_manager_insights: ["Look for passion and curiosity", "Portfolio projects matter more than degrees", "Cultural fit is key"],
          application_checklist: ["Tailored Resume", "Portfolio/Case Studies", "Cover Letter", "LinkedIn Profile Update"]
        },
        portfolio_projects: [
          { title: "Industry Analysis Project", difficulty: "Medium", time_estimate: "2 weeks", description: "Conduct a deep dive into a specific industry trend.", why_relevant: "Demonstrates analytical skills." },
          { title: "Process Improvement Plan", difficulty: "Hard", time_estimate: "3 weeks", description: "Propose a solution to a common operational bottleneck.", why_relevant: "Shows problem-solving ability." }
        ],
        pathways: {
          stable: { description: "Traditional corporate ladder progression.", steps: ["Junior Associate", "Senior Associate", "Manager", "Director"] },
          adventurous: { description: "High-growth startup environment.", steps: ["Join early stage", "Wear multiple hats", "Lead a department"] },
          risky: { description: "Entrepreneurial or freelance route.", steps: ["Build a personal brand", "Launch a consulting practice", "Scale your business"] }
        },
        guided_questions: ["What is the typical salary range?", "What are the remote work options?", "How fast is the promotion cycle?"]
      });
    } finally {
      setLoadingDeepDive(false);
    }
  };

  const handleSendMessage = async (message = null) => {
    const messageToSend = message || chatMessage;
    if (!messageToSend.trim() || sendingMessage) return;

    // Immediately add user message to chat so it appears right away
    const currentChat = role?.chat || [];
    setRole(prev => ({
      ...prev,
      chat: [...currentChat, { role: 'user', content: messageToSend }]
    }));
    setChatMessage('');
    setSendingMessage(true);
    setChatError('');

    try {
      // If we don't have a roadmapId, use the generic chat endpoint
      if (!roadmapId) {
        const onboardingData = localStorage.getItem('onboardingData');
        const parsedData = onboardingData ? JSON.parse(onboardingData) : {};

        const response = await fetch(`${BACKEND_URL}/api/chat/generic`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            role_data: role,
            message: messageToSend,
            chat_history: currentChat,
            user_context: parsedData
          })
        });

        if (!response.ok) {
          throw new Error(`Failed to send message: ${response.status}`);
        }

        const data = await response.json();

        // Backend returns { success: true, message: "AI response" }
        // We need to build the full chat history ourselves
        if (data.message) {
          setRole(prev => ({
            ...prev,
            chat: [...(prev?.chat || []), { role: 'assistant', content: data.message }]
          }));
        } else if (data.chat_history || data.chat) {
          setRole(prev => ({
            ...prev,
            chat: data.chat_history || data.chat
          }));
        }
        setSendingMessage(false);
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/roadmaps/${roadmapId}/roles/${roleId}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            message: messageToSend,
            role_data: role // Pass full role context for self-healing if ID is missing in DB
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to send message: ${response.status}`);
      }

      const data = await response.json();
      // Update with full chat history from server (includes user message + AI response)
      setRole(prev => ({
        ...prev,
        chat: data.chat_history || data.chat || prev?.chat
      }));
    } catch (error) {
      console.error('Error sending message:', error);
      setChatError('Unable to send a message right now. Using offline guide mode.');
      // Add offline AI response (user message already shown)
      const offlineReply = "I'm here to help! Ask about skills, projects, or what a day looks like in this role and I'll share guidance.";
      setRole(prev => ({
        ...prev,
        chat: [...(prev?.chat || []), { role: 'assistant', content: offlineReply }]
      }));
    } finally {
      setSendingMessage(false);
    }
  };

  const handleQuestionClick = (question) => {
    handleSendMessage(question);
    // Scroll to chat
    const chatSection = document.querySelector('.chat-section');
    if (chatSection) {
      chatSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getSkillBadge = (status) => {
    switch(status) {
      case 'has':
        return <Badge className="skill-badge has-skill"><CheckCircle2 className="w-3 h-3 mr-1" />Has</Badge>;
      case 'partial':
        return <Badge className="skill-badge partial-skill"><AlertCircle className="w-3 h-3 mr-1" />Partial</Badge>;
      case 'missing':
        return <Badge className="skill-badge missing-skill"><X className="w-3 h-3 mr-1" />Missing</Badge>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="role-detail-page">
        <div className="role-detail-container">
          <div className="loading-state">
            <p>Loading role details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!role) {
    return (
      <div className="role-detail-page">
        <div className="role-detail-container">
          <div className="error-state">
            <p>Role not found</p>
            <Button onClick={() => navigate(`/roadmap/${roadmapId}`)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Roadmap
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const chat = role.chat || [];

  return (
    <div
      className="role-detail-page"
    >
      {/* Fixed background layer - GPU accelerated */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(/bluewhite.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          zIndex: -1,
          transform: 'translateZ(0)',
        }}
      />
      {/* Header */}
      <header className="role-page-header">
        <div className="role-detail-container flex justify-between items-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => roadmapId ? navigate(`/roadmap/${roadmapId}`) : navigate('/discover')}
            className="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {roadmapId ? 'Back to Roadmap' : 'Back to Discover'}
          </Button>
          
          <Button
            variant={isSaved ? "default" : "outline"}
            size="sm"
            onClick={handleSaveRole}
            className={`ml-auto ${isSaved ? 'bg-green-500 hover:bg-green-600 text-white' : 'bg-white/10 text-white hover:bg-white/20 border-white/20'}`}
          >
            <Bookmark className={`w-4 h-4 mr-2 ${isSaved ? 'fill-current' : ''}`} />
            {isSaved ? 'Saved' : 'Save Role'}
          </Button>
        </div>
      </header>

      <div className="role-detail-container main-content">
        {/* Title and Description Card */}
        <Card className="role-info-card">
          <div className="role-info-content">
            <h1 className="role-title">{role.title}</h1>
            {role.vibe_title && (
              <p className="text-lg font-semibold text-purple-400 italic mb-2">aka "{role.vibe_title}"</p>
            )}
            <p className="role-description">{role.description}</p>
          </div>
        </Card>

        {/* Video Carousel */}
        <div className="section-spacer">
          <VideoCarousel videos={videos} loading={loadingVideos} roleTitle={role.title} />
        </div>

        {/* Deep Dive Content */}
        {loadingDeepDive ? (
          <div className="loading-deep-dive">
            <div className="loading-spinner"></div>
            <p>Generating personalized career analysis...</p>
          </div>
        ) : deepDive ? (
          <div className="deep-dive-content">
            {/* 1. What This Career Actually Is */}
            <section className="deep-dive-section">
              <button className="section-header" onClick={() => toggleSection('overview')}>
                <h2 className="section-title">What This Career Actually Is</h2>
                {expandedSections.overview ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              {expandedSections.overview && (
                <div className="section-content">
                  <p className="career-overview-text">{deepDive.career_overview.what_it_is}</p>
                  
                  <div className="info-grid">
                    <div className="info-block">
                      <h3 className="info-block-title">Day in the Life</h3>
                      <ul className="info-list">
                        {deepDive.career_overview.day_in_the_life.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="info-block">
                      <h3 className="info-block-title">What You'll Work On</h3>
                      <ul className="info-list">
                        {deepDive.career_overview.what_youll_work_on.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="info-block">
                      <h3 className="info-block-title">Where This Role Exists</h3>
                      <ul className="info-list">
                        {deepDive.career_overview.where_this_role_exists.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Top Companies Hiring Now */}
                  {deepDive.top_companies && deepDive.top_companies.length > 0 && (
                    <div className="mt-8 pt-8 border-t border-black/10">
                      <h3 className="subsection-title">Top Companies Hiring Now</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {deepDive.top_companies.map((company, idx) => (
                          <a 
                            key={idx} 
                            href={company.link || `https://www.google.com/search?q=jobs+at+${encodeURIComponent(company.name)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="company-card group"
                          >
                            <div className="flex justify-between items-start mb-2">
                              <h3 className="company-name group-hover:text-[#5a7a50] transition-colors">
                                {company.name}
                                <ExternalLink className="w-3 h-3 opacity-50" />
                              </h3>
                              <Badge variant="outline" className="bg-[#86a17d]/10 text-[#5a7a50] border-[#86a17d]/20">
                                {company.hiring_status || 'Hiring'}
                              </Badge>
                            </div>
                            <p className="company-industry">{company.industry}</p>
                            <p className="company-description">{company.description}</p>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </section>

            {/* 2. Why You're a Good Fit */}
            <section className="deep-dive-section">
              <button className="section-header" onClick={() => toggleSection('goodFit')}>
                <h2 className="section-title">Why You're a Good Fit</h2>
                {expandedSections.goodFit ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              {expandedSections.goodFit && (
                <div className="section-content">
                  <ul className="good-fit-list">
                    {deepDive.why_youre_a_good_fit.map((item, idx) => (
                      <li key={idx} className="good-fit-item">
                        <CheckCircle2 className="w-4 h-4 good-fit-icon" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>

            {/* 3. What You Need to Apply */}
            <section className="deep-dive-section">
              <button className="section-header" onClick={() => toggleSection('apply')}>
                <h2 className="section-title">What You Need to Apply</h2>
                {expandedSections.apply ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              {expandedSections.apply && (
                <div className="section-content">
                  {/* Required Skills */}
                  <div className="apply-subsection">
                    <h3 className="subsection-title">Required Skills</h3>
                    <div className="skills-grid">
                      {deepDive.what_you_need_to_apply.required_skills.map((skill, idx) => (
                        <div key={idx} className="skill-item">
                          <div className="flex justify-between items-start w-full">
                            <span className="skill-name">{skill.skill}</span>
                            {getSkillBadge(skill.status)}
                          </div>
                          <p className="skill-reason">{skill.reason}</p>
                          
                          {/* Learning Resource for missing/partial skills */}
                          {(skill.status === 'missing' || skill.status === 'partial') && (
                            <a 
                              href={skill.learning_resource?.link || `https://www.coursera.org/search?query=${encodeURIComponent(skill.skill)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="link-preview-card group"
                            >
                              <div className="link-preview-image">
                                <BookOpen className="w-6 h-6" />
                              </div>
                              <div className="link-preview-content">
                                <div className="link-preview-title">
                                  Learn {skill.skill}
                                </div>
                                <div className="link-preview-domain">
                                  {skill.learning_resource?.name || 'Coursera'}
                                  <ExternalLink className="w-3 h-3" />
                                </div>
                              </div>
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Hiring Manager Insights */}
                  <div className="apply-subsection">
                    <h3 className="subsection-title">What Hiring Managers Look For</h3>
                    <ul className="insights-list">
                      {deepDive.what_you_need_to_apply.hiring_manager_insights.map((insight, idx) => (
                        <li key={idx}>{insight}</li>
                      ))}
                    </ul>
                  </div>

                  {/* Application Checklist */}
                  <div className="apply-subsection">
                    <h3 className="subsection-title">Application Requirements Checklist</h3>
                    <ul className="checklist">
                      {deepDive.what_you_need_to_apply.application_checklist.map((item, idx) => (
                        <li key={idx} className="checklist-item">
                          <div className="checklist-checkbox" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </section>

            {/* 4. Portfolio & Side Project Examples */}
            <section className="deep-dive-section">
              <button className="section-header" onClick={() => toggleSection('portfolio')}>
                <h2 className="section-title">Portfolio & Side Project Examples</h2>
                {expandedSections.portfolio ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              {expandedSections.portfolio && (
                <div className="section-content">
                  <div className="projects-grid">
                    {deepDive.portfolio_projects.map((project, idx) => (
                      <div key={idx} className="project-card">
                        <div className="project-header">
                          <h4 className="project-title">{project.title}</h4>
                          <div className="project-meta">
                            <Badge className={`difficulty-badge ${project.difficulty.toLowerCase()}`}>
                              {project.difficulty}
                            </Badge>
                            <span className="time-estimate">
                              <Clock className="w-3 h-3" />
                              {project.time_estimate}
                            </span>
                          </div>
                        </div>
                        <p className="project-description">{project.description}</p>
                        <p className="project-relevance">{project.why_relevant}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>

            {/* 5. Pathways Into This Role */}
            <section className="deep-dive-section">
              <button className="section-header" onClick={() => toggleSection('pathways')}>
                <h2 className="section-title">Pathways Into This Role</h2>
                {expandedSections.pathways ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              {expandedSections.pathways && (
                <div className="section-content">
                  <div className="pathways-grid">
                    <div className="pathway-card stable-path">
                      <h3 className="pathway-title">Stable Path</h3>
                      <p className="pathway-description">{deepDive.pathways.stable.description}</p>
                      <ul className="pathway-steps">
                        {deepDive.pathways.stable.steps.map((step, idx) => (
                          <li key={idx}>{step}</li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="pathway-card adventurous-path">
                      <h3 className="pathway-title">Adventurous Path</h3>
                      <p className="pathway-description">{deepDive.pathways.adventurous.description}</p>
                      <ul className="pathway-steps">
                        {deepDive.pathways.adventurous.steps.map((step, idx) => (
                          <li key={idx}>{step}</li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="pathway-card risky-path">
                      <h3 className="pathway-title">Risky Path</h3>
                      <p className="pathway-description">{deepDive.pathways.risky.description}</p>
                      <ul className="pathway-steps">
                        {deepDive.pathways.risky.steps.map((step, idx) => (
                          <li key={idx}>{step}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </section>
          </div>
        ) : (
          <div className="error-state">
            <p>Unable to load career deep-dive</p>
          </div>
        )}

        {/* Chat Interface */}
        <Card className="chat-card chat-section">
          <div>
            <h2 className="chat-title">Chat with a Simulated {role.title}</h2>
            <p className="chat-subtitle">Ask questions about the role, get guidance, and learn what it's like to work in this position</p>
            
            <div className="chat-messages" ref={chatMessagesRef}>
              {chat.length === 0 ? (
                <div className="chat-empty">
                  <p className="mb-4">Start a conversation! Ask about the role, required skills, or what a typical day looks like.</p>
                  
                  {/* Suggested Questions */}
                  {deepDive?.guided_questions && (
                    <div className="flex flex-wrap gap-2 justify-center mt-4">
                      {deepDive.guided_questions.slice(0, 4).map((question, idx) => (
                        <button
                          key={idx}
                          className="text-xs bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-full transition-colors border border-white/10"
                          onClick={() => handleQuestionClick(question)}
                        >
                          {question}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                chat.map((message, index) => {
                  // Split assistant messages on ||| to create separate bubbles
                  if (message.role === 'assistant' && message.content.includes('|||')) {
                    const bubbles = message.content.split('|||').map(b => b.trim()).filter(Boolean);
                    return bubbles.map((bubble, bubbleIdx) => (
                      <div
                        key={`${index}-${bubbleIdx}`}
                        className="chat-message assistant-message"
                      >
                        <div className="message-content">{bubble}</div>
                      </div>
                    ));
                  }
                  return (
                    <div
                      key={index}
                      className={`chat-message ${message.role === 'user' ? 'user-message' : 'assistant-message'}`}
                    >
                      <div className="message-content">
                        {message.content}
                      </div>
                    </div>
                  );
                })
              )}
              {sendingMessage && (
                <div className="chat-message assistant-message">
                  <div className="message-content">Thinking...</div>
                </div>
              )}
            </div>

            {chatError && (
              <p className="text-xs text-red-200 px-4 pb-2">
                {chatError}
              </p>
            )}

            {/* More suggested questions if chat has started */}
            {chat.length > 0 && deepDive?.guided_questions && (
              <div className="px-4 pb-2 overflow-x-auto flex gap-2 no-scrollbar">
                {deepDive.guided_questions.map((question, idx) => (
                  <button
                    key={idx}
                    className="text-xs bg-white/5 hover:bg-white/10 text-gray-300 px-3 py-1.5 rounded-full transition-colors border border-white/5 whitespace-nowrap flex-shrink-0"
                    onClick={() => handleQuestionClick(question)}
                  >
                    {question}
                  </button>
                ))}
              </div>
            )}

            <div className="chat-input-container">
              <Input
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                placeholder="Ask a question about this role..."
                className="chat-input"
                disabled={sendingMessage}
              />
              <Button
                onClick={() => handleSendMessage()}
                disabled={!chatMessage.trim() || sendingMessage}
                className="send-button"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default RoleDetailPage;
